import { decode } from '@googlemaps/polyline-codec';

export const METERS_PER_DEGREE_LAT = 111_139;
export const metersPerDegreeLng = (lat) => METERS_PER_DEGREE_LAT * Math.cos((lat * Math.PI) / 180);

/**
 * Resolve a set of [lat, lng] pairs from route/active tour data.
 * @param {any} routeData
 * @param {any} activeTourData
 * @returns {Array<[number, number]>}
 */
export function resolveRoutePoints(routeData, activeTourData) {
  const normalizePoints = (points) => {
    if (!Array.isArray(points) || !points.length) return [];
    if (Array.isArray(points[0])) return points;
    if (typeof points[0] === 'object') {
      return points
        .map((pt) => {
          const lat = Number(pt?.lat ?? pt?.latitude ?? pt?.[0]);
          const lng = Number(pt?.lng ?? pt?.longitude ?? pt?.[1]);
          return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
        })
        .filter(Boolean);
    }
    return [];
  };

  const decodePolyline = (polyline) => {
    if (!polyline) return [];
    if (Array.isArray(polyline)) return normalizePoints(polyline);
    if (typeof polyline === 'string') {
      try {
        return decode(polyline);
      } catch {
        return [];
      }
    }
    return [];
  };

  const appendPoints = (acc, next) => {
    if (!next.length) return acc;
    if (!acc.length) return [...next];
    const last = acc[acc.length - 1];
    const first = next[0];
    if (last && first && last[0] === first[0] && last[1] === first[1]) {
      return acc.concat(next.slice(1));
    }
    return acc.concat(next);
  };

  if (routeData?.polyline) return decodePolyline(routeData.polyline);
  if (activeTourData?.encodedPolyline) return decodePolyline(activeTourData.encodedPolyline);
  if (activeTourData?.polyline) return decodePolyline(activeTourData.polyline);

  const legSources = routeData?.legs || activeTourData?.legs || activeTourData?.nodes || [];
  if (Array.isArray(legSources) && legSources.length) {
    return legSources.reduce((acc, leg) => {
      const legPolyline = leg?.polyline || leg?.encodedPolyline || null;
      const points = decodePolyline(legPolyline);
      return appendPoints(acc, points);
    }, []);
  }

  return [];
}

/**
 * Compute cumulative and total distances along a polyline using haversine.
 * @param {Array<[number, number]>} routePoints
 * @returns {{ cum: number[], total: number }}
 */
export function computeRouteMetrics(routePoints) {
  if (!routePoints || routePoints.length < 2) return { cum: [], total: 0 };
  let total = 0;
  const cum = [0];
  const toRad = (v) => (v * Math.PI) / 180;
  for (let i = 0; i < routePoints.length - 1; i++) {
    const a = routePoints[i];
    const b = routePoints[i + 1];
    const dLat = toRad(b[0] - a[0]);
    const dLng = toRad(b[1] - a[1]);
    const lat1 = toRad(a[0]);
    const lat2 = toRad(b[0]);
    const h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
    const dist = 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)));
    total += dist;
    cum.push(total);
  }
  return { cum, total };
}

/**
 * Project a point onto a polyline and return along-distance and squared distance.
 * @param {Array<[number, number]>} routePoints
 * @param {{ cum: number[], total: number }} routeMetrics
 * @param {{ lat: number, lng: number }} target
 * @returns {{ along: number, dist2: number }}
 */
export function projectLatLngToRoute(routePoints, routeMetrics, target) {
  if (
    !routePoints?.length ||
    routeMetrics?.total <= 0 ||
    target?.lat == null ||
    target?.lng == null
  ) {
    return { along: 0, dist2: Number.POSITIVE_INFINITY };
  }
  const refLat = target.lat;
  const mLat = METERS_PER_DEGREE_LAT;
  const mLng = metersPerDegreeLng(refLat);
  const toXY = (pt) => ({ x: pt[1] * mLng, y: pt[0] * mLat });
  const p = toXY([target.lat, target.lng]);
  let best = { dist2: Number.POSITIVE_INFINITY, along: 0 };
  for (let i = 0; i < routePoints.length - 1; i++) {
    const aPt = routePoints[i];
    const bPt = routePoints[i + 1];
    const a = toXY(aPt);
    const b = toXY(bPt);
    const vx = b.x - a.x;
    const vy = b.y - a.y;
    const len2 = vx * vx + vy * vy;
    if (len2 === 0) continue;
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * vx + (p.y - a.y) * vy) / len2));
    const projX = a.x + vx * t;
    const projY = a.y + vy * t;
    const dx = p.x - projX;
    const dy = p.y - projY;
    const dist2 = dx * dx + dy * dy;
    if (dist2 < best.dist2) {
      const segLen = Math.sqrt(len2);
      const along = (routeMetrics.cum[i] || 0) + segLen * t;
      best = { dist2, along };
    }
  }
  return best;
}

/**
 * Convert an along-distance into ETA using total route duration.
 * @param {number} along
 * @param {{ total: number }} routeMetrics
 * @param {number|null} totalDurationSeconds
 * @returns {number|null}
 */
export function etaFromAlong(along, routeMetrics, totalDurationSeconds) {
  if (!Number.isFinite(totalDurationSeconds) || routeMetrics.total <= 0) return null;
  const clamped = Math.max(0, Math.min(routeMetrics.total, along || 0));
  return (totalDurationSeconds * clamped) / routeMetrics.total;
}

/**
 * Convenience helper to project a point and derive ETA.
 * @param {Array<[number, number]>} routePoints
 * @param {{ cum: number[], total: number }} routeMetrics
 * @param {number|null} totalDurationSeconds
 * @param {{ lat: number, lng: number }} target
 * @returns {{ along: number, etaSeconds: number|null }}
 */
export function projectWithEta(routePoints, routeMetrics, totalDurationSeconds, target) {
  const proj = projectLatLngToRoute(routePoints, routeMetrics, target);
  const etaSeconds = etaFromAlong(proj.along, routeMetrics, totalDurationSeconds);
  const along = Math.max(0, Math.min(routeMetrics.total || 0, proj.along || 0));
  return { along, etaSeconds };
}
